import { useEffect, useRef, useCallback } from 'react'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import '../../styles/tour.css'

const STORAGE_KEY = 'powertron-tour-completed'

// ---------------------------------------------------------------------------
// Step pool — all possible spotlight steps, keyed for reuse across roles
// ---------------------------------------------------------------------------
const STEPS = {
  dashboard: {
    element: '[data-tour="tour-dashboard"]',
    side: 'right',
    align: 'start'
  },
  roi: {
    element: '[data-tour="tour-roi"]',
    side: 'right',
    align: 'start'
  },
  eas: {
    element: '[data-tour="tour-eas"]',
    side: 'right',
    align: 'start'
  },
  explorer: {
    element: '[data-tour="tour-explorer"]',
    side: 'right',
    align: 'start'
  },
  analysis: {
    element: '[data-tour="tour-analysis"]',
    side: 'right',
    align: 'start'
  },
  fleet: {
    element: '[data-tour="tour-fleet"]',
    side: 'right',
    align: 'start'
  },
  glossary: {
    element: '[data-tour="tour-glossary"]',
    side: 'left',
    align: 'end'
  },
  ai: {
    element: '[data-tour="tour-ai"]',
    side: 'right',
    align: 'start'
  }
}

// ---------------------------------------------------------------------------
// Role definitions for the selector UI
// ---------------------------------------------------------------------------
const ROLES = [
  { id: 'evaluator', icon: '\u{1F4CB}', label: 'Evaluating for Purchase', desc: 'Find proof, compare sites, model ROI' },
  { id: 'engineering', icon: '\u2699', label: 'Engineering / Technical', desc: 'Data, methodology, analysis tools' },
  { id: 'finance', icon: '\u{1F4C8}', label: 'Finance / Leadership', desc: 'ROI, fleet impact, executive summaries' },
  { id: 'explore', icon: '\u{1F9ED}', label: 'Show Me Everything', desc: 'Full tour of all features' }
]

// ---------------------------------------------------------------------------
// Role-specific tour sequences — each role gets tailored titles/descriptions
// ---------------------------------------------------------------------------
const ROLE_TOURS = {
  evaluator: [
    {
      key: 'dashboard',
      title: 'Verified Performance Data',
      description: '174 independently verified deployments across 24 states. Filter by equipment type, industry, or region to find sites like yours.'
    },
    {
      key: 'explorer',
      title: 'Find Similar Deployments',
      description: 'Search and filter all case studies by equipment, tonnage, industry, and location to find the most comparable results.'
    },
    {
      key: 'roi',
      title: 'Model Your Savings',
      description: 'Enter your equipment details to see projected savings and payback period, grounded in real-world performance data.'
    },
    {
      key: 'glossary',
      title: 'Technical Reference',
      description: 'Look up any metric \u2014 kW/Ton, COP, EER, and more. Includes a "Start Here" guide for evaluators new to HVAC data.'
    },
    {
      key: 'ai',
      title: 'Ask Anything',
      description: 'Try: "What results do chillers in Texas see?" Every answer includes source citations from the corpus.'
    }
  ],

  engineering: [
    {
      key: 'dashboard',
      title: 'Key Efficiency Metrics',
      description: 'Statistical overview with confidence intervals, standard deviation, and sample sizes. Filter by refrigerant, equipment type, or manufacturer.'
    },
    {
      key: 'analysis',
      title: 'Analysis Suite',
      description: 'Four specialized views: ASHRAE longevity data, equipment breakdowns, PE-certified engineering metrics, and efficiency trend analysis.'
    },
    {
      key: 'explorer',
      title: 'Data Explorer',
      description: 'Filter all 174 studies by equipment type, tonnage, refrigerant, state, and efficiency range. Export comparable datasets.'
    },
    {
      key: 'eas',
      title: 'Equipment Assessment',
      description: 'Upload your own equipment data (spreadsheet or manual entry) for per-unit analysis with methodology documentation.'
    },
    {
      key: 'ai',
      title: 'Technical Q&A',
      description: 'Ask technical questions with source-cited answers. Try: "What\'s the average kW/Ton improvement for R-22 chillers?"'
    }
  ],

  finance: [
    {
      key: 'dashboard',
      title: 'Performance at a Glance',
      description: '29.8% average efficiency gain, $120/ton annual savings, validated across 174 deployments. The numbers that matter.'
    },
    {
      key: 'roi',
      title: 'ROI Calculator',
      description: 'Model financial returns by equipment \u2014 savings, payback period, and 5-year projection. CFO-ready output.'
    },
    {
      key: 'eas',
      title: 'Portfolio Assessment',
      description: 'Upload your equipment list for a per-unit ROI breakdown. Generates an executive summary with aggregate savings.'
    },
    {
      key: 'fleet',
      title: 'Fleet-Wide Impact',
      description: 'Analyze savings across your entire equipment fleet. See total cost reduction, payback timeline, and CO2 impact at scale.'
    },
    {
      key: 'ai',
      title: 'Financial Q&A',
      description: 'Ask financial questions. Try: "What\'s typical payback for rooftop units?" or "How much can a 50-ton chiller save annually?"'
    }
  ],

  explore: [
    {
      key: 'dashboard',
      title: 'Dashboard Overview',
      description: 'Your starting point. Key metrics, charts, geographic coverage, and quick tools \u2014 all filterable by equipment, industry, and refrigerant.'
    },
    {
      key: 'roi',
      title: 'ROI Calculator',
      description: 'Model financial returns for your equipment. Enter tonnage, energy use, and electricity rate to see projected savings and payback period.'
    },
    {
      key: 'eas',
      title: 'EAS Calculator',
      description: 'Upload your own equipment data (spreadsheet or manual entry) to generate a customized Equipment Assessment Summary with per-unit analysis.'
    },
    {
      key: 'explorer',
      title: 'Data Explorer',
      description: 'Search and filter all 174 case studies. Find equipment similar to yours by type, tonnage, industry, or location.'
    },
    {
      key: 'analysis',
      title: 'Analysis Suite',
      description: 'Four specialized views: ASHRAE longevity data, equipment breakdown, engineering metrics, and efficiency trends over time.'
    },
    {
      key: 'glossary',
      title: 'Glossary & Help',
      description: 'Available on every page. Look up kW/Ton, COP, EER, and other metrics. Includes a "Start Here" guide for new users.'
    },
    {
      key: 'ai',
      title: 'AI Assistant',
      description: 'Ask any question about the corpus data. Powered by the full dataset with source citations in every answer.'
    }
  ]
}

// ---------------------------------------------------------------------------
// Build driver.js steps from a role sequence
// ---------------------------------------------------------------------------
function buildSteps(role) {
  const sequence = ROLE_TOURS[role]
  return sequence.map(step => ({
    element: STEPS[step.key].element,
    popover: {
      title: step.title,
      description: step.description,
      side: STEPS[step.key].side,
      align: STEPS[step.key].align
    }
  }))
}

// ---------------------------------------------------------------------------
// Build role selector DOM (safe DOM methods, no innerHTML)
// ---------------------------------------------------------------------------
function buildRoleSelectorDOM(popover, onRoleSelect) {
  const container = popover.description

  const subtitle = document.createElement('p')
  subtitle.className = 'tour-role-subtitle'
  subtitle.textContent = 'Choose your path for a personalized tour:'
  container.appendChild(subtitle)

  const grid = document.createElement('div')
  grid.className = 'tour-role-grid'

  ROLES.forEach(role => {
    const btn = document.createElement('button')
    btn.className = 'tour-role-btn'
    btn.setAttribute('data-role', role.id)

    const icon = document.createElement('span')
    icon.className = 'tour-role-icon'
    icon.textContent = role.icon
    btn.appendChild(icon)

    const label = document.createElement('span')
    label.className = 'tour-role-label'
    label.textContent = role.label
    btn.appendChild(label)

    const desc = document.createElement('span')
    desc.className = 'tour-role-desc'
    desc.textContent = role.desc
    btn.appendChild(desc)

    btn.addEventListener('click', () => onRoleSelect(role.id))
    grid.appendChild(btn)
  })

  container.appendChild(grid)
}

// ---------------------------------------------------------------------------
// Shared driver config
// ---------------------------------------------------------------------------
const BASE_CONFIG = {
  showProgress: true,
  progressText: '{{current}} of {{total}}',
  nextBtnText: 'Next',
  prevBtnText: 'Back',
  doneBtnText: 'Get Started',
  allowClose: true,
  overlayColor: '#0F1419',
  overlayOpacity: 0.75,
  stagePadding: 8,
  stageRadius: 8,
  popoverOffset: 12,
  smoothScroll: true,
  animate: true,
  popoverClass: 'powertron-tour-popover'
}

// ---------------------------------------------------------------------------
// Inject "Don't show again" checkbox as its own row above the footer
// ---------------------------------------------------------------------------
function injectDontShowCheckbox(popover) {
  const wrapper = popover.wrapper
  const footer = popover.footer
  if (!wrapper) return

  const row = document.createElement('label')
  row.className = 'tour-dont-show'

  const checkbox = document.createElement('input')
  checkbox.type = 'checkbox'
  checkbox.className = 'tour-dont-show-checkbox'
  checkbox.id = 'tour-dont-show-input'
  row.appendChild(checkbox)

  const text = document.createElement('span')
  text.className = 'tour-dont-show-text'
  text.textContent = "Don't show this tour again"
  row.appendChild(text)

  // Insert before footer if it exists, otherwise append to wrapper
  if (footer) {
    wrapper.insertBefore(row, footer)
  } else {
    wrapper.appendChild(row)
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function OnboardingTour() {
  const driverRef = useRef(null)

  const destroyCurrent = useCallback(() => {
    if (driverRef.current) {
      // Remove onDestroyed to avoid setting localStorage during role switch
      driverRef.current.setConfig({ ...driverRef.current.getConfig(), onDestroyed: undefined })
      driverRef.current.destroy()
      driverRef.current = null
    }
  }, [])

  const maybePersistDismiss = useCallback(() => {
    const checkbox = document.getElementById('tour-dont-show-input')
    if (checkbox && checkbox.checked) {
      localStorage.setItem(STORAGE_KEY, 'true')
    }
    driverRef.current = null
  }, [])

  const startRoleTour = useCallback((role) => {
    destroyCurrent()

    setTimeout(() => {
      const steps = buildSteps(role)

      // Add checkbox to every step so user can opt out at any point
      steps.forEach(step => {
        step.popover.onPopoverRender = (popover) => {
          injectDontShowCheckbox(popover)
        }
      })

      const driverInstance = driver({
        ...BASE_CONFIG,
        steps,
        onDestroyed: maybePersistDismiss
      })

      driverRef.current = driverInstance
      driverInstance.drive()
    }, 100)
  }, [destroyCurrent, maybePersistDismiss])

  const showRoleSelector = useCallback(() => {
    destroyCurrent()

    setTimeout(() => {
      const selectorDriver = driver({
        ...BASE_CONFIG,
        showProgress: false,
        steps: [{
          popover: {
            title: 'Welcome \u2014 How Can We Help?',
            description: ' ',
            showButtons: ['close'],
            popoverClass: 'powertron-tour-popover powertron-tour-role-selector',
            onPopoverRender: (popover) => {
              buildRoleSelectorDOM(popover, (role) => {
                startRoleTour(role)
              })
              injectDontShowCheckbox(popover)
            }
          }
        }],
        onDestroyed: maybePersistDismiss
      })

      driverRef.current = selectorDriver
      selectorDriver.drive()
    }, 300)
  }, [destroyCurrent, startRoleTour])

  // Auto-start on first visit
  useEffect(() => {
    const completed = localStorage.getItem(STORAGE_KEY) === 'true'
    if (!completed) {
      showRoleSelector()
    }
  }, [showRoleSelector])

  // Listen for restart-tour event from sidebar
  useEffect(() => {
    const handleRestart = () => {
      localStorage.removeItem(STORAGE_KEY)
      showRoleSelector()
    }
    window.addEventListener('restart-tour', handleRestart)
    return () => window.removeEventListener('restart-tour', handleRestart)
  }, [showRoleSelector])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy()
      }
    }
  }, [])

  return null
}

export default OnboardingTour
